import { observer } from "mobx-react-lite";
import { FC, useCallback } from "react";
import { PlatformIconKind } from "@/domains/api/types";
import { useAppBannerLinkOnClickHandler } from "@/components/banners/app-callout-banner/utils/app-banner-link-handling";
import { TrackedEvent, trackEvent } from "@/domains/metrics";
import type {
  AppCalloutBannerActionSlot,
  AppCalloutBannerEventParams,
} from "@/domains/metrics/tracked-event";
import { AppCalloutBannerActionButtonView } from "@/components/banners/app-callout-banner/app-callout-banner-action-button-view";

interface AppCalloutBannerActionButtonProps {
  iconKind: PlatformIconKind | null;
  label: string | null;
  url: string | null;
  buttonVariant?: string | null;
  actionSlot: AppCalloutBannerActionSlot;
  trackingContext: AppCalloutBannerEventParams;
  onActionClick?: () => void;
  onActionComplete?: () => void;
}

const runAfterOpenedWindowReturns = ({
  openedWindow,
  onReturn,
}: {
  openedWindow: Window;
  onReturn: () => void;
}) => {
  let hasRun = false;
  let closedWindowIntervalId: number | null = null;

  const cleanup = () => {
    window.removeEventListener("focus", run);

    if (closedWindowIntervalId !== null) {
      window.clearInterval(closedWindowIntervalId);
      closedWindowIntervalId = null;
    }
  };

  const run = () => {
    if (hasRun) {
      return;
    }

    hasRun = true;
    cleanup();
    onReturn();
  };

  if (openedWindow.closed) {
    run();
    return;
  }

  window.addEventListener("focus", run, { once: true });
  closedWindowIntervalId = window.setInterval(() => {
    if (openedWindow.closed) {
      run();
    }
  }, 200);
};

export const AppCalloutBannerActionButton: FC<AppCalloutBannerActionButtonProps> = observer(
  ({
    iconKind,
    label,
    url,
    buttonVariant,
    actionSlot,
    trackingContext,
    onActionClick,
    onActionComplete,
  }) => {
    const { onClickHandler } = useAppBannerLinkOnClickHandler({
      url: url || "",
      actionSlot,
      trackingContext,
    });
    const onClick = useCallback(() => {
      trackEvent(TrackedEvent.ChromeExtensionHomeCarouselInstallerClick, { label });
      onActionClick?.();

      void (async () => {
        const openResult = await onClickHandler();

        if (!onActionComplete) {
          return;
        }

        if (!openResult.didOpen) {
          onActionComplete();
          return;
        }

        if (!openResult.openedWindow) {
          window.setTimeout(onActionComplete, 0);
          return;
        }

        runAfterOpenedWindowReturns({
          openedWindow: openResult.openedWindow,
          onReturn: onActionComplete,
        });
      })();
    }, [label, onActionClick, onActionComplete, onClickHandler]);

    if (!label) return null;
    if (!url) return null;

    return (
      <AppCalloutBannerActionButtonView
        iconKind={iconKind}
        label={label}
        buttonVariant={buttonVariant}
        onClick={onClick}
      />
    );
  }
);
